import { useState } from 'react'
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, Input, Label } from '../ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui'
import { toast } from 'sonner'

export type Chain = 'BTC' | 'BCH' | 'LTC' | 'ETH' | 'TRON' | 'BSC' | 'POLYGON' | 'SOLANA'

export interface WhitelistedAddress {
  id: string
  address: string
  chain: string
  label?: string | null
}

interface AddWhitelistedAddressModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  companyId: string
  existingAddresses: WhitelistedAddress[]
  onCreateWhitelistedAddress: (variables: {
    input: { companyId: string; chain: any; address: string; label: string }
  }) => Promise<{ data?: unknown }>
}

interface FormErrors {
  title?: string
  address?: string
  chain?: string
}

export function AddWhitelistedAddressModal({
  open,
  onOpenChange,
  companyId,
  existingAddresses,
  onCreateWhitelistedAddress,
}: AddWhitelistedAddressModalProps) {
  const [title, setTitle] = useState('')
  const [address, setAddress] = useState('')
  const [chain, setChain] = useState<Chain | ''>('')
  const [errors, setErrors] = useState<FormErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    // Title validation
    if (!title.trim()) {
      newErrors.title = 'Title is required'
    } else {
      // Check if title already exists
      const titleExists = existingAddresses.some(
        (addr) => addr.label?.toLowerCase() === title.trim().toLowerCase(),
      )
      if (titleExists) {
        newErrors.title = 'This name is already taken'
      }
    }

    // Address validation
    if (!address.trim()) {
      newErrors.address = 'Address is required'
    } else {
      // Check if address already exists
      const addressExists = existingAddresses.some(
        (addr) =>
          addr.chain === chain && addr.address.toLowerCase() === address.trim().toLowerCase(),
      )
      if (addressExists) {
        newErrors.address = 'This address is already whitelisted'
      } else {
        // Basic address validation based on chain
        const trimmedAddress = address.trim()
        if (chain === 'BTC') {
          // Bitcoin address validation (basic)
          if (!/^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$|^bc1[a-z0-9]{39,59}$/.test(trimmedAddress)) {
            newErrors.address = 'Please enter a valid BTC address'
          }
        } else if (chain === 'BCH') {
          // Bitcoin Cash address validation (legacy format or CashAddr format)
          if (
            !/^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$|^(bitcoincash:)?(q|p)[a-z0-9]{41}$/.test(
              trimmedAddress,
            )
          ) {
            newErrors.address = 'Please enter a valid BCH address'
          }
        } else if (chain === 'LTC') {
          // Litecoin address validation (native SegWit ltc1... or legacy L.../M...)
          if (!/^ltc1[a-z0-9]{39,59}$|^[LM][a-km-zA-HJ-NP-Z1-9]{26,33}$/.test(trimmedAddress)) {
            newErrors.address = 'Please enter a valid LTC address'
          }
        } else if (chain === 'ETH') {
          // Ethereum address validation
          if (!/^0x[a-fA-F0-9]{40}$/.test(trimmedAddress)) {
            newErrors.address = 'Please enter a valid ETH address'
          }
        } else if (chain === 'BSC') {
          // BSC address validation (EVM)
          if (!/^0x[a-fA-F0-9]{40}$/.test(trimmedAddress)) {
            newErrors.address = 'Please enter a valid BSC address'
          }
        } else if (chain === 'POLYGON') {
          if (!/^0x[a-fA-F0-9]{40}$/.test(trimmedAddress)) {
            newErrors.address = 'Please enter a valid Polygon address'
          }
        } else if (chain === 'SOLANA') {
          if (!/^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(trimmedAddress)) {
            newErrors.address = 'Please enter a valid Solana address'
          }
        } else if (chain === 'TRON') {
          // Tron address validation
          if (!/^T[A-Za-z1-9]{33}$/.test(trimmedAddress)) {
            newErrors.address = 'Please enter a valid TRON address'
          }
        } else if (trimmedAddress.length < 10) {
          newErrors.address = 'Please enter a valid address'
        }
      }
    }

    if (!chain) {
      newErrors.chain = 'Chain is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)
    try {
      const result = await onCreateWhitelistedAddress({
        input: {
          companyId,
          chain: chain,
          address: address.trim(),
          label: title.trim(),
        },
      })

      if (result.data) {
        toast.success('Whitelisted address has been added successfully!')
        handleClose()
      } else {
        toast.error('Something went wrong. Please try again.')
      }
    } catch (error) {
      console.error('Error creating whitelisted address:', error)
      toast.error('Something went wrong. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setTitle('')
    setAddress('')
    setChain('')
    setErrors({})
    setIsSubmitting(false)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader className="flex flex-row items-center justify-between space-y-0 pb-6">
          <DialogTitle className="text-xl font-semibold">Add Whitelisted Address</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value)
                if (errors.title) {
                  setErrors((prev) => ({ ...prev, title: undefined }))
                }
              }}
              placeholder=""
              className={errors.title ? 'border-red-500' : ''}
            />
            {errors.title && <p className="text-sm text-red-600">{errors.title}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">Address</Label>
            <Input
              id="address"
              value={address}
              onChange={(e) => {
                setAddress(e.target.value)
                if (errors.address) {
                  setErrors((prev) => ({ ...prev, address: undefined }))
                }
              }}
              placeholder=""
              className={errors.address ? 'border-red-500' : ''}
            />
            {errors.address && <p className="text-sm text-red-600">{errors.address}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="chain">Chain</Label>
            <Select
              value={chain}
              onValueChange={(value) => {
                setChain(value as Chain)
                if (errors.chain) {
                  setErrors((prev) => ({ ...prev, chain: undefined }))
                }
              }}
            >
              <SelectTrigger className={errors.chain ? 'border-red-500' : ''}>
                <SelectValue placeholder="Select chain" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="BTC">BTC</SelectItem>
                <SelectItem value="BCH">BCH</SelectItem>
                <SelectItem value="LTC">LTC</SelectItem>
                <SelectItem value="ETH">ETH</SelectItem>
                <SelectItem value="BSC">BSC</SelectItem>
                <SelectItem value="POLYGON">Polygon</SelectItem>
                <SelectItem value="SOLANA">Solana</SelectItem>
                <SelectItem value="TRON">TRON</SelectItem>
              </SelectContent>
            </Select>
            {errors.chain && <p className="text-sm text-red-600">{errors.chain}</p>}
          </div>

          <div className="flex justify-end pt-4">
            <Button
              variant="main_action"
              className="rounded-md w-full"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Adding...' : 'Add'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
